/**
 * Risk panel — composite risk ranking by country/region.
 *
 * Subscribes to analysis:risk events and displays regions ranked
 * by composite risk score with CII, Convergence, and Infrastructure
 * component breakdown. Click a row to fly the map there.
 */

import { eventBus } from '@/core/events';
import { getAnalysisEngine } from '@/intelligence/analysis/engine';
import { getMap } from '@/map/engine';

export function createRiskPanel(): HTMLElement {
  const container = document.createElement('div');
  container.className = 'risk-panel';
  container.style.cssText = 'display: flex; flex-direction: column; height: 100%; overflow-y: auto;';

  const contentArea = document.createElement('div');
  contentArea.style.cssText = 'padding: 12px;';
  container.appendChild(contentArea);

  const cleanupFns: (() => void)[] = [];
  let renderPending = false;

  function scheduleRender(): void {
    if (renderPending) return;
    renderPending = true;
    requestAnimationFrame(() => {
      renderPending = false;
      render();
    });
  }

  function render(): void {
    const engine = getAnalysisEngine();
    const result = engine?.getLastResult();
    const risks = result?.compositeRisks ?? [];

    contentArea.textContent = '';

    if (risks.length === 0) {
      const msg = document.createElement('div');
      msg.style.cssText = 'padding: 32px 16px; text-align: center; color: var(--text-muted); font-size: 12px;';
      msg.textContent = 'No composite risk scores available yet';
      contentArea.appendChild(msg);
      return;
    }

    const header = document.createElement('div');
    header.style.cssText = 'font-size: 11px; font-weight: 600; color: var(--text-secondary); margin-bottom: 8px; text-transform: uppercase; letter-spacing: 0.05em;';
    header.textContent = `Risk Ranking (${risks.length})`;
    contentArea.appendChild(header);

    const sorted = risks.slice().sort((a, b) => b.score - a.score);

    for (const risk of sorted) {
      const row = document.createElement('div');
      row.style.cssText = `
        padding: 8px 10px; margin-bottom: 6px; border-radius: 6px;
        background: var(--bg-secondary); border: 1px solid var(--border-primary);
        cursor: pointer;
      `;
      row.title = 'Click to fly to location';

      row.onclick = () => {
        const map = getMap();
        if (map && risk.location) {
          map.flyTo({
            center: [risk.location.lng, risk.location.lat],
            zoom: Math.max(map.getZoom(), 5),
            duration: 800,
          });
        }
      };

      // Name + score
      const titleRow = document.createElement('div');
      titleRow.style.cssText = 'display: flex; align-items: center; gap: 8px; margin-bottom: 6px;';

      const name = document.createElement('span');
      name.style.cssText = 'font-size: 12px; font-weight: 600; color: var(--text-primary);';
      name.textContent = risk.regionName;
      titleRow.appendChild(name);

      const scoreColor = risk.score >= 70 ? 'var(--severity-critical)'
        : risk.score >= 50 ? 'var(--severity-high)'
          : risk.score >= 30 ? 'var(--severity-medium)'
            : 'var(--severity-low)';

      // Score bar
      const barBg = document.createElement('div');
      barBg.style.cssText = 'flex: 1; height: 6px; border-radius: 3px; background: var(--bg-primary);';
      const barFill = document.createElement('div');
      barFill.style.cssText = `height: 100%; border-radius: 3px; width: ${risk.score}%; background: ${scoreColor}; transition: width 300ms ease;`;
      barBg.appendChild(barFill);
      titleRow.appendChild(barBg);

      const scoreNum = document.createElement('span');
      scoreNum.style.cssText = `font-size: 12px; font-weight: 700; font-family: var(--font-mono); color: ${scoreColor}; min-width: 24px; text-align: right;`;
      scoreNum.textContent = String(Math.round(risk.score));
      titleRow.appendChild(scoreNum);

      row.appendChild(titleRow);

      // Component breakdown
      const components = document.createElement('div');
      components.style.cssText = 'display: flex; gap: 8px; font-size: 10px;';

      const ciiPct = document.createElement('span');
      ciiPct.style.cssText = 'color: var(--text-muted);';
      ciiPct.textContent = `CII ${Math.round(risk.components.cii)}%`;
      components.appendChild(ciiPct);

      const convPct = document.createElement('span');
      convPct.style.cssText = 'color: var(--text-muted);';
      convPct.textContent = `Conv ${Math.round(risk.components.convergence)}%`;
      components.appendChild(convPct);

      const infraPct = document.createElement('span');
      infraPct.style.cssText = 'color: var(--text-muted);';
      infraPct.textContent = `Infra ${Math.round(risk.components.infrastructure)}%`;
      components.appendChild(infraPct);

      // Boosts
      if (risk.boosts.theaterPosture > 0 || risk.boosts.breakingNews > 0) {
        const boostLabel = document.createElement('span');
        boostLabel.style.cssText = 'color: var(--severity-high); margin-left: auto;';
        const boosts: string[] = [];
        if (risk.boosts.theaterPosture > 0) boosts.push(`theater +${risk.boosts.theaterPosture}`);
        if (risk.boosts.breakingNews > 0) boosts.push(`breaking +${risk.boosts.breakingNews}`);
        boostLabel.textContent = boosts.join(', ');
        components.appendChild(boostLabel);
      }

      row.appendChild(components);
      contentArea.appendChild(row);
    }
  }

  // Initial render
  render();

  // Subscribe to analysis:complete
  cleanupFns.push(
    eventBus.on('analysis:complete', () => scheduleRender()),
  );

  (container as HTMLElement & { destroy?: () => void }).destroy = () => {
    for (const fn of cleanupFns) fn();
    cleanupFns.length = 0;
  };

  return container;
}
